import * as React from 'react';
import { Box, Button } from '@chakra-ui/react';
import { InfoTip, Tooltip, Toaster } from '@ui';
import { IoIosEye } from 'react-icons/io';
import type { TabOpenButtonProps } from './types';
import {
  darkPdfViewerTheme,
  lightPdfViewerTheme,
  generatedDarkStyles,
  generatedLightStyles,
} from './themes';



export const TabOpenButton: React.FC<TabOpenButtonProps> = ({
  url,
  pdfTitle,
  pageTitle = 'Document Preview',
  details = [],
  buttonLabel = 'Open Preview',
  buttonIcon = <IoIosEye />,
  disabled = false,
  isInfoTip = true,
  theme = 'dark',
  buttonProps,
  onClick,
  onBeforeOpen,
  ...rest
}) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleOpen = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      try {
        setIsLoading(true);

        let effectiveUrl = url;
        let effectivePdfTitle = pdfTitle;
        let effectivePageTitle = pageTitle;
        let effectiveDetails = details;

        if (onBeforeOpen) {
          const result = await onBeforeOpen(e);
          if (typeof result === 'string') {
            effectiveUrl = result || effectiveUrl;
          } else if (result && typeof result === 'object') {
            effectiveUrl = result.url ?? effectiveUrl;
            effectivePdfTitle = result.pdfTitle ?? effectivePdfTitle;
            effectivePageTitle = result.pageTitle ?? effectivePageTitle;
            effectiveDetails = result.details ?? effectiveDetails;
          }
        }

        if (!effectiveUrl) return;

        if (onClick) {
          onClick(e);
          return;
        }

        const styles = theme === 'dark' ? generatedDarkStyles : generatedLightStyles;

        const detailsHtml = (effectiveDetails || [])
          .map(
            (d) =>
              `<div class="item"><div class="label">${String(d.label)}</div><div class="value">${
                typeof d.value === 'string' || typeof d.value === 'number'
                  ? d.value
                  : ''
              }</div></div>`,
          )
          .join('');

        const html = `<!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <title>${effectivePageTitle} - ${effectivePdfTitle}</title>
            <style>${styles}</style>
          </head>
          <body>
            <div class="container">
              <div class="header">
                <div class="title">${effectivePageTitle}</div>
              </div>
              <div class="details">${detailsHtml}</div>
              <div class="frameWrap">
                <iframe src="${effectiveUrl}" allowfullscreen title="${effectivePdfTitle}"></iframe>
              </div>
            </div>
          </body>
        </html>`;

        const blob = new Blob([html], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } catch (error) {
        console.error(error);
        Toaster.create({
          type: 'error',
          title: 'Failed to open',
          message: 'There was a problem preparing the document.',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [url, pdfTitle, pageTitle, details, theme, onClick, onBeforeOpen],
  );


  return (
    <Tooltip content={!disabled ? '' : 'No file to open'}>
      <Box>
        <Button
          aria-label="open-pdf-in-new-tab"
          onClick={ handleOpen }
          fontSize="xs"
          variant="outline"
          whiteSpace="nowrap"
          colorPalette={"secondary"}
          {...buttonProps}
          {...rest}
          disabled={disabled || isLoading}
        >
          {buttonIcon}
          {buttonLabel}
        </Button>
        {isInfoTip && <InfoTip>"Will open pdf in a new tab"</InfoTip>}
      </Box>
    </Tooltip>
  );
};
